import { isValidPhoneNumber } from 'libphonenumber-js'

import { isEmailSyntaxValid } from '@orus.eu/email'
import { type Language, translate } from '@orus.eu/translations'

type ValidationResult<VALUE> = { ok: true; value: VALUE } | { ok: false; hint: string }

export const validationUrlSchemes = ['http:', 'https:', 'mailto:', 'tel:'] as const

export type ValidationUrlScheme = (typeof validationUrlSchemes)[number]

export function validateInteger(text: string, language: Language): ValidationResult<number> {
  text = text.trim().replace(/ /g, '')
  if (!text) {
    return { ok: false, hint: translate('error_mandatory_field', language) }
  }
  if (/^[0-9]+$/.test(text)) {
    if (Number(text) > Number.MAX_SAFE_INTEGER || Number(text) < Number.MIN_SAFE_INTEGER) {
      return { ok: false, hint: translate('error_invalid_field', language) }
    }

    return { ok: true, value: parseInt(text, 10) }
  }
  if (/^[0-9.,]+$/.test(text)) {
    return { ok: false, hint: translate('error_integer_no_decimals', language) }
  }

  return { ok: false, hint: translate('error_integer_generic', language) }
}

export function validatePositiveInteger(value: string, language: Language): ValidationResult<number> {
  const isValidInteger = validateInteger(value, language)

  if (!isValidInteger.ok || isValidInteger.value > 0) {
    return isValidInteger
  }

  return { ok: false, hint: translate('error_positive_integer', language) }
}

export function validateEmail(text: string, language: Language): ValidationResult<string> {
  text = text.trim().toLowerCase()
  if (!text) {
    return { ok: false, hint: translate('error_mandatory_field', language) }
  }

  const isValidEmail = isEmailSyntaxValid(text)
  if (!isValidEmail) {
    return { ok: false, hint: translate('error_email_generic', language) }
  }

  return { ok: true, value: text }
}

export function validateUrl(
  text: string,
  allowedSchemes: readonly ValidationUrlScheme[],
  language: Language,
): ValidationResult<string> {
  text = text.trim()

  if (!text) {
    return { ok: false, hint: translate('error_mandatory_field', language) }
  }

  const schemePattern = new RegExp(`^(${allowedSchemes.join('|')})`)
  const match = text.match(schemePattern)

  if (!match) {
    return { ok: false, hint: `L'url doit commencer par ${formatAllowedSchemes(allowedSchemes)}` }
  }

  const scheme = match[0]

  if (scheme === 'mailto:') {
    const email = text.slice(scheme.length)
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailPattern.test(email)) {
      return { ok: false, hint: "Le format de l'adresse email est invalide" }
    }
  } else if (scheme === 'tel:') {
    const phone = text.slice(scheme.length)
    const phonePattern = /^\+?[0-9\s\-().]+$/
    if (!phonePattern.test(phone)) {
      return { ok: false, hint: 'Le numéro de téléphone est invalide' }
    }
  } else if (scheme === 'http:' || scheme === 'https:') {
    const urlPattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/

    if (!urlPattern.test(text)) {
      return { ok: false, hint: "Le format de l'url est invalide" }
    }

    if (!/^https?:\/\//.test(text)) {
      return { ok: false, hint: "L'url doit commencer par http:// ou https://" }
    }
  }

  return { ok: true, value: text }
}

/**
 * Validate a phone number.
 *
 * @param {string} text
 * @returns {ValidationResult<string>}
 */
export function validatePhoneNumber(text: string, language: Language): ValidationResult<string> {
  text = text.trim()
  if (!text) {
    return { ok: false, hint: translate('error_mandatory_field', language) }
  }

  if (!isValidPhoneNumber(text, 'FR')) {
    return { ok: false, hint: translate('error_phone_generic', language) }
  }

  return { ok: true, value: text }
}

function formatAllowedSchemes(schemes: readonly ValidationUrlScheme[]): string {
  const leadingSchemes = schemes.slice(0, -1)
  const lastScheme = schemes.slice(-1).join('')
  return leadingSchemes.length === 0 ? lastScheme : `${leadingSchemes.join(', ')} ou ${lastScheme}`
}
